import { RENDER_DEBUG } from "./debug-flags";
/**
 * Shared view cache for useZeroSelector hooks.
 *
 * Maintains one TypedView per cache key (query identity). Ref-counted —
 * view is created on first subscriber, destroyed when last subscriber
 * leaves (with TTL delay to avoid destroy/recreate churn on remounts).
 *
 * Modeled after Zero's internal ViewStore (use-query.js:144-178).
 */

interface CachedView {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  view: any; // TypedView — typed as any to avoid importing internal types
  refCount: number;
  destroyTimer: ReturnType<typeof setTimeout> | null;
}

const DESTROY_DELAY_MS = 5000; // Keep view alive 5s after last subscriber leaves

// Global cache — shared across all useZeroSelector instances
const cache = new Map<string, CachedView>();

/**
 * Acquire a shared view for a query. Returns the view and a release function.
 * Multiple callers with the same key share one view.
 *
 * @param zero - The Zero client instance
 * @param key - Unique cache key (e.g., "users.all" or "stats.byUser")
 * @param query - The Zero query to materialize
 * @param ttl - Time-to-live for the view (Zero TTL string, e.g., "5m")
 */
export function acquireView(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zero: any,
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any,
  ttl?: string,
): { view: CachedView["view"]; release: () => void } {
  let cached = cache.get(key);

  if (cached) {
    // Cancel pending destroy if someone re-subscribes
    if (cached.destroyTimer !== null) {
      clearTimeout(cached.destroyTimer);
      cached.destroyTimer = null;
      if (RENDER_DEBUG)
        console.debug(
          `[ViewCache] REUSE view for "${key}" (cancelled destroy, refCount=${cached.refCount + 1})`,
        );
    } else {
      if (RENDER_DEBUG)
        console.debug(
          `[ViewCache] SHARE view for "${key}" (refCount=${cached.refCount + 1})`,
        );
    }
    cached.refCount++;
    return { view: cached.view, release: () => releaseView(key) };
  }

  // Create new view
  if (RENDER_DEBUG) console.debug(`[ViewCache] CREATE view for "${key}"`);
  const view = zero.materialize(query, ttl ? { ttl } : undefined);
  cached = { view, refCount: 1, destroyTimer: null };
  cache.set(key, cached);

  return { view, release: () => releaseView(key) };
}

function releaseView(key: string) {
  const cached = cache.get(key);
  if (!cached) return;

  cached.refCount--;
  if (cached.refCount > 0) return;

  // Last subscriber left — schedule destroy with delay
  if (RENDER_DEBUG)
    console.debug(
      `[ViewCache] RELEASE "${key}" (refCount=${cached.refCount}, scheduling destroy)`,
    );
  cached.destroyTimer = setTimeout(() => {
    // Re-check: someone may have re-acquired during the delay
    const current = cache.get(key);
    if (current && current.refCount <= 0) {
      current.view.destroy();
      cache.delete(key);
    }
  }, DESTROY_DELAY_MS);
}

/**
 * Clear all cached views. For testing only.
 */
export function clearViewCache() {
  for (const [, cached] of cache) {
    if (cached.destroyTimer !== null) clearTimeout(cached.destroyTimer);
    cached.view.destroy();
  }
  cache.clear();
}
